import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { AUTH_MODES, DEFAULT_AGENT_CONFIG, PERMISSION_MODES, type ActorDTO, type AgentConfig, type FallbackRuntime, type McpServerConfig, type NodeDTO, type ProjectDTO, type SubagentConfig } from "@roomate/core";
import { useAgentSkills, useSetAgentSkills, useSkills, useActorRuntime, useActors, useNodes, useAgentConfig, useAgentImportPreview, useAgentImports, useMe, usePatch, useProjects, useRollbackAgentImport } from "../api.ts";
import { MOCK_IMPORT_PREVIEW, MOCK_IMPORT_VERSIONS } from "../agent-import.ts";
import { ADAPTER_OPTIONS, canEditRuntime } from "../agent-adapter.ts";
import { RUNTIME_LABEL } from "../lib/run-runtime.ts";
import { fallbackCandidates, isClaudeAlias, isSeatRuntime, isSelectable, modelChoices, moveItem, runtimeAvailability, runtimeCapabilities, SEAT_RUNTIMES, SEAT_STATE_LABEL, unavailableWarning, withoutPrimary } from "../lib/seats.ts";
import { AdapterLogoMark } from "../components/AdapterLogo.tsx";
import { initialDraft, planAssignment } from "../agent-projects.ts";
import { Action, Block, Card, Page, PageHeader, Pill } from "../calm.tsx";
import { Button, cx, ErrorNote, inputCls } from "../ui.tsx";
import { Dialog, Field } from "./Project.tsx";
import { AgentImportStart } from "../components/AddNew.tsx";
import { DiffView } from "./AgentImport.tsx";
import { assignableSkills } from "../lib/skills-ui.ts";
import type { SkillListItemDTO } from "../components/skills/skills.ts";

/** How a node runs this agent: model, budget, tools, MCP servers and standing instructions. */
export function AgentConfigButton({ actor }: { actor: ActorDTO }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <span className="flex items-center gap-0.5">
        <Button variant="ghost" className="px-2 text-[12px]" onClick={() => setOpen(true)}>Run config</Button>
        <Link to={`/agents/${actor.id}/config`} className="rounded-md px-2 py-1.5 text-[12px] text-ink-2 hover:bg-paper-2" data-testid="agent-config-link">Imports</Link>
      </span>
      {open && (
        <Dialog title={`How ${actor.name} runs`} onClose={() => setOpen(false)}>
          <AgentConfigForm key={actor.id} actor={actor} onSaved={() => setOpen(false)} />
        </Dialog>
      )}
    </>
  );
}

export function AgentConfigPage() {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const actor = (useActors().data ?? []).find((item) => item.id === id);
  const importsQuery = useAgentImports(id);
  const rollback = useRollbackAgentImport();
  const [showReimport, setShowReimport] = useState(false);
  const [tab, setTab] = useState<"preview" | "diff">("preview");
  const demo = params.get("demo") === "import";
  const versions = demo ? MOCK_IMPORT_VERSIONS.map((item) => ({ ...item, actorId: id })) : importsQuery.data ?? [];
  // Don't assume the list is sorted — pick by version number, not list position.
  const latestVersion = versions.length ? versions.reduce((a, b) => (b.version > a.version ? b : a)) : undefined;
  // The diff lives on the stored preview for a version, not on the lightweight AgentImportDTO list
  // itself — refetch it for the latest version so the Diff tab reflects a real re-import, not just the demo.
  const latestVersionId = demo ? "" : latestVersion?.id ?? "";
  const latestPreview = useAgentImportPreview(latestVersionId);
  if (!actor) return <Page><p className="text-[13px] text-ink-3">Agent not found.</p></Page>;
  return (
    <Page wide>
      <PageHeader
        crumbs={[{ label: "Team", to: "/team" }, { label: actor.name }]}
        title={`${actor.name} config`}
        meta={<span>Run settings, imported source, changes and rollback history.</span>}
        actions={<Action primary onClick={() => setShowReimport((open) => !open)} testId="reimport-agent">Re-import</Action>}
      />
      <div className="mt-8 space-y-8">
        {showReimport && <Block title="Re-import Claude folder" aside="The current version keeps running until approvals are complete"><Card><AgentImportStart actorId={actor.id} onClose={() => setShowReimport(false)} /></Card></Block>}
        {actor.kind === "agent" && (
          <Block title="Adapter" aside="Which subscription runs this agent">
            <Card><AdapterControl actor={actor} /></Card>
          </Block>
        )}
        {actor.kind === "agent" && (
          <Block title="Projects" aside="Where routing may offer this agent">
            <Card><ProjectsControl actor={actor} /></Card>
          </Block>
        )}
        {actor.kind === "agent" && (
          <Block title="Skills" aside="Approved library skills this agent gets on its next run">
            <Card><SkillsControl actor={actor} /></Card>
          </Block>
        )}
        <Block title="Imported configuration" aside="Preview the current mapping or inspect changes">
          <Card>
            <div className="mb-5 flex gap-1 border-b border-rule" role="tablist">
              <button role="tab" aria-selected={tab === "preview"} onClick={() => setTab("preview")} className={cx("border-b-2 px-3 py-2 text-[13px]", tab === "preview" ? "border-ink text-ink" : "border-transparent text-ink-3")} data-testid="config-preview-tab">Preview</button>
              <button role="tab" aria-selected={tab === "diff"} onClick={() => setTab("diff")} className={cx("border-b-2 px-3 py-2 text-[13px]", tab === "diff" ? "border-ink text-ink" : "border-transparent text-ink-3")} data-testid="config-diff-tab">Diff</button>
            </div>
            {tab === "preview" ? (
              <div className="grid gap-4 text-[13px] md:grid-cols-3">
                <div><p className="text-ink-3">Source</p><p className="mt-1 font-mono">{latestVersion?.source?.kind ?? "No import yet"}</p></div>
                <div><p className="text-ink-3">Instructions</p><p className="mt-1 tabular-nums">{actor.agentConfig?.instructions.length.toLocaleString() ?? 0} characters</p></div>
                <div><p className="text-ink-3">Fence</p><p className="mt-1 font-mono">{actor.allowedAreas.length ? actor.allowedAreas.join(", ") : "unfenced"}</p></div>
              </div>
            ) : demo ? <DiffView diff={MOCK_IMPORT_PREVIEW.diff} /> : latestVersionId ? <DiffView diff={latestPreview.data?.diff ?? null} /> : <p className="py-8 text-center text-[13px] italic text-ink-3">A diff appears here after you create a re-import preview.</p>}
          </Card>
        </Block>
        <Block title="Version history" aside={`${versions.length} version${versions.length === 1 ? "" : "s"}`}>
          <Card pad={false}>
            {versions.length ? <div className="overflow-x-auto"><table className="w-full text-left text-[12.5px]" data-testid="import-version-history"><thead className="border-b border-rule text-ink-3"><tr><th className="px-5 py-3 font-medium">Version</th><th className="px-5 py-3 font-medium">Source</th><th className="px-5 py-3 font-medium">Commit</th><th className="px-5 py-3 font-medium">Who · when</th><th className="px-5 py-3 font-medium">Status</th><th className="px-5 py-3"><span className="sr-only">Actions</span></th></tr></thead><tbody>
              {versions.map((version) => <tr key={version.id} className="border-b border-rule/60 last:border-0"><td className="px-5 py-3 font-mono">v{version.version}</td><td className="max-w-[260px] px-5 py-3"><span className="block truncate" title={version.source?.url ?? version.source?.path}>{version.source?.kind ?? "unknown"}{version.source?.url ? ` · ${version.source.url}` : ""}</span></td><td className="px-5 py-3 font-mono">{version.source?.sha?.slice(0, 8) ?? "—"}</td><td className="px-5 py-3">{version.createdByName ?? version.createdBy} · <time title={new Date(version.createdAt).toLocaleString()}>{new Date(version.createdAt).toLocaleDateString()}</time></td><td className="px-5 py-3"><Pill tone={version.status === "applied" ? "done" : version.status === "pending_approval" ? "signal" : "neutral"}>{version.status.replace("_", " ")}</Pill></td><td className="px-5 py-3 text-right"><Button disabled={version.status !== "superseded" || rollback.isPending} onClick={() => rollback.mutate({ importId: version.id, actorId: actor.id })} data-testid={`rollback-${version.version}`}>Roll back</Button></td></tr>)}
            </tbody></table></div> : <p className="px-5 py-8 text-center text-[13px] italic text-ink-3">No imported versions yet. Re-import creates the first version.</p>}
          </Card>
          <ErrorNote error={importsQuery.error ?? rollback.error ?? latestPreview.error} />
        </Block>
        <Block title="Run configuration" aside="Manual settings remain available for this agent"><Card><AgentConfigForm key={actor.id} actor={actor} onSaved={() => undefined} /></Card></Block>
      </div>
    </Page>
  );
}

/** The agent's runtime (claude-code / codex), as a pair of calm pills instead of a raw select.
 *  Saves on click through PATCH /api/actors/:id — a sibling field of agentConfig, not part of it,
 *  so it is its own control rather than folded into AgentConfigForm's submit. */
export function AdapterControl({ actor }: { actor: ActorDTO }) {
  const me = useMe().data!;
  const update = useActorRuntime();
  const editable = canEditRuntime(me, actor);
  const nodes = useNodes().data ?? [];
  const [setAnyway, setSetAnyway] = useState(false);
  const current = actor.runtime;
  // Runtime has more values than this control offers (vscode, other — set at import or
  // creation). Say the real value up front so no card silently looks "selected" or "unset"
  // for those, and a click away from this line is an informed replacement, not a guess.
  const knownOption = isSeatRuntime(current);
  const warning = unavailableWarning(nodes, actor.id, current, { apiKey: actor.agentConfig?.auth === "api_key" });
  return (
    <div>
      {!knownOption && (
        <p className="mb-2.5 text-[12px] text-ink-3">
          Currently <span className="font-mono text-ink-2">{current ?? "unset"}</span> — not one of the adapters below. Picking one replaces it.
        </p>
      )}
      <div className="grid gap-2 sm:grid-cols-2">
        {SEAT_RUNTIMES.map((runtime) => {
          const option = ADAPTER_OPTIONS.find((o) => o.value === runtime);
          const selected = current === runtime;
          const av = runtimeAvailability(nodes, actor.id, runtime, { apiKey: actor.agentConfig?.auth === "api_key" });
          const allowed = isSelectable(av) || setAnyway || selected;
          return (
            <button
              key={runtime}
              type="button"
              aria-pressed={selected}
              disabled={!editable || update.isPending || !allowed}
              onClick={() => update.mutate({ id: actor.id, runtime })}
              data-testid={`adapter-${runtime}`}
              className={cx(
                "rounded-xl border px-3.5 py-2.5 text-left text-[12.5px] transition-colors disabled:cursor-not-allowed",
                selected ? "border-ink bg-ink text-paper" : "border-rule",
                (!editable || !allowed) && "opacity-60",
              )}
            >
              <span className="flex items-center gap-2 font-medium">
                {option && <AdapterLogoMark logo={option.logo} />}
                {RUNTIME_LABEL[runtime]}
              </span>
              <span className={cx("mt-1 block text-[11.5px]", selected ? "text-paper/70" : "text-ink-3")} data-testid={`adapter-availability-${runtime}`}>
                {av.state === "ready" ? `Ready on ${av.node}` : av.state === "connect" ? "Not connected" : SEAT_STATE_LABEL[av.state]}
              </span>
              {option?.note && <span className={cx("mt-0.5 block text-[11.5px]", selected ? "text-paper/70" : "text-ink-3")}>{option.note}</span>}
            </button>
          );
        })}
      </div>
      <label className="mt-2.5 flex items-center gap-2 text-[12.5px] text-ink-2">
        <input type="checkbox" checked={setAnyway} onChange={(e) => setSetAnyway(e.target.checked)} disabled={!editable} data-testid="adapter-set-anyway" />
        Set anyway (choose a runtime that is not ready yet)
      </label>
      {warning && <p className="mt-2 rounded-md bg-signal-soft px-3 py-2 text-[12px] text-ink-2" data-testid="adapter-unavailable-warning">{warning}</p>}
      <p className="mt-2 text-[12px] text-ink-3">{editable ? "Takes effect on the agent's next run." : "Only an admin or this agent's owner can change this."}</p>
      <ErrorNote error={update.error} />
    </div>
  );
}

/** Library skills for one agent. Reads and writes the same agent_skills rows as the Skills page picker, so both show one state after a reload. */
export function SkillsControl({ actor }: { actor: ActorDTO }) {
  const me = useMe().data;
  const library = useSkills();
  const assigned = useAgentSkills(actor.id);
  if ((library.isError && !library.data) || (assigned.isError && !assigned.data)) return <ErrorNote error={library.error ?? assigned.error} />;
  if (!library.data || !assigned.data) return <p className="text-[13px] text-ink-3">Loading skills…</p>;
  return <SkillsEditor key={`${actor.id}:${[...assigned.data.skillIds].sort().join(",")}`} actor={actor} skills={[...library.data, ...(assigned.data.skills ?? []).filter((s) => !library.data.some((l) => l.id === s.id))]} saved={assigned.data.skillIds} editable={me?.access === "admin"} />;
}

function SkillsEditor({ actor, skills, saved, editable }: { actor: ActorDTO; skills: SkillListItemDTO[]; saved: string[]; editable: boolean }) {
  const save = useSetAgentSkills();
  const [picked, setPicked] = useState(saved);
  const toggle = (id: string) => {
    if (save.isPending) return;
    save.reset();
    setPicked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };
  const dirty = [...picked].sort().join() !== [...saved].sort().join();
  return (
    <div>
      <SkillsChecklist skills={skills} saved={saved} picked={picked} editable={editable && !save.isPending} onToggle={toggle} />
      {editable ? (
        <div className="mt-3 flex justify-end">
          <Button variant="primary" disabled={!dirty || save.isPending} onClick={() => save.mutate({ id: actor.id, skillIds: picked })} data-testid="skills-save">Save skills</Button>
        </div>
      ) : (
        <p className="mt-2 text-[12px] text-ink-3">Only an admin can change this.</p>
      )}
      <ErrorNote error={save.error} />
    </div>
  );
}

/** The checkbox list itself: approved, non-archived skills, plus any already assigned one so an assignment is never hidden. */
export function SkillsChecklist({ skills, saved, picked, editable, onToggle }: { skills: SkillListItemDTO[]; saved: string[]; picked: string[]; editable: boolean; onToggle: (id: string) => void }) {
  const choices = assignableSkills(skills, saved);
  if (choices.length === 0) return <p className="text-[13px] text-ink-3" data-testid="skills-empty">No approved skills yet. <Link to="/skills" className="underline decoration-rule hover:text-ink">Add one in the Skills library.</Link></p>;
  return (
    <fieldset>
      <legend className="sr-only">Skills this agent gets</legend>
      <ul className="space-y-1.5" data-testid="agent-skills">
        {choices.map((s) => (
          <li key={s.id}>
            <label className="flex items-center gap-2.5 text-[13px] text-ink-2">
              <input type="checkbox" checked={picked.includes(s.id)} disabled={!editable} onChange={() => onToggle(s.id)} data-testid={`skill-check-${s.id}`} />
              {s.name}
              {s.archivedAt !== null && <span className="text-[12px] text-ink-3">(archived)</span>}
              {s.approvedVersion === null && <span className="text-[12px] text-ink-3">(not approved)</span>}
            </label>
          </li>
        ))}
      </ul>
    </fieldset>
  );
}

/** Which projects an agent may be routed to. "All projects" saves `[]`; a checklist saves its ids.
 *  Waits for the project list before showing anything, so the checklist and Save are never computed
 *  against an empty list. Non-admins get the saved assignment read-only. */
export function ProjectsControl({ actor }: { actor: ActorDTO }) {
  const meQuery = useMe();
  const projects = useProjects();
  // Only a failure with nothing cached replaces the editor; a background refetch error keeps the draft.
  if ((projects.isError && !projects.data) || (meQuery.isError && !meQuery.data)) return <ErrorNote error={projects.error ?? meQuery.error} />;
  const me = meQuery.data;
  if (!me || projects.isPending) return <p className="text-[13px] text-ink-3">Loading projects…</p>;
  // Keyed on the agent and its saved assignment: moving to another agent, a save, or another admin's
  // change remounts with a fresh draft. Agents with no projects share the same ids, so the id must be in the key.
  return <ProjectsEditor key={`${actor.id}:${[...actor.projectIds].sort().join(",")}`} actor={actor} projects={projects.data} editable={me.access === "admin"} />;
}

function ProjectsEditor({ actor, projects, editable }: { actor: ActorDTO; projects: ProjectDTO[]; editable: boolean }) {
  const save = usePatch<ActorDTO>([["actors"]]);
  const saved = actor.projectIds;
  const [draft, setDraft] = useState(() => initialDraft(saved));
  const known = projects.map((project) => project.id);
  const names = projects.filter((project) => saved.includes(project.id)).map((project) => project.name);
  const deleted = saved.length - names.length;

  if (!editable) {
    return (
      <div>
        <p className="text-[13px] text-ink-2" data-testid="projects-readonly">
          {saved.length === 0 ? "All projects" : [names.join(", "), deleted ? `${deleted} deleted` : ""].filter(Boolean).join(" + ")}
        </p>
        <p className="mt-2 text-[12px] text-ink-3">
          {saved.length === 0 ? "Routing offers this agent every project. " : "Routing only offers this agent for the checked projects. "}Only an admin can change this.
        </p>
      </div>
    );
  }

  const { specific, picked } = draft;
  const plan = planAssignment(draft, saved, known);
  // Editing clears a previous save error, but never detaches a save in flight: its result must still
  // be reported. The controls are also disabled while saving, so this guard is a second line.
  const clearError = () => {
    if (!save.isPending) save.reset();
  };
  const toggle = (id: string) => {
    if (save.isPending) return;
    clearError();
    setDraft((current) => ({ ...current, picked: current.picked.includes(id) ? current.picked.filter((x) => x !== id) : [...current.picked, id] }));
  };
  const setMode = (next: boolean) => {
    if (save.isPending) return;
    clearError();
    setDraft((current) => ({ ...current, specific: next }));
  };

  return (
    <div>
      <div className="grid gap-2 sm:grid-cols-2" role="group" aria-label="Which projects">
        <button
          type="button"
          aria-pressed={!specific}
          onClick={() => setMode(false)}
          disabled={save.isPending}
          data-testid="projects-all"
          className={cx("rounded-xl border px-3.5 py-2.5 text-left text-[12.5px] transition-colors", !specific ? "border-ink bg-ink text-paper" : "border-rule")}
        >
          <span className="block font-medium">All projects</span>
          <span className={cx("mt-1 block text-[11.5px]", !specific ? "text-paper/70" : "text-ink-3")}>Includes projects created later.</span>
        </button>
        <button
          type="button"
          aria-pressed={specific}
          onClick={() => setMode(true)}
          disabled={save.isPending}
          data-testid="projects-specific"
          className={cx("rounded-xl border px-3.5 py-2.5 text-left text-[12.5px] transition-colors", specific ? "border-ink bg-ink text-paper" : "border-rule")}
        >
          <span className="block font-medium">Only these projects</span>
          <span className={cx("mt-1 block text-[11.5px]", specific ? "text-paper/70" : "text-ink-3")}>Pick a checklist below.</span>
        </button>
      </div>
      {specific && (
        <fieldset className="mt-3">
          <legend className="sr-only">Projects this agent may be routed to</legend>
          <ul className="space-y-1.5">
            {projects.map((project) => (
              <li key={project.id}>
                <label className="flex items-center gap-2.5 text-[13px] text-ink-2">
                  <input type="checkbox" checked={picked.includes(project.id)} disabled={save.isPending} onChange={() => toggle(project.id)} data-testid={`project-check-${project.id}`} />
                  {project.name}
                </label>
              </li>
            ))}
          </ul>
        </fieldset>
      )}
      <p className="mt-3 text-[12px] text-ink-3">
        {specific ? "Routing only offers this agent for the checked projects." : "Routing offers this agent every project, including ones created later."}
        {specific && plan.invalid ? " Check at least one project, or pick All projects." : ""}
        {deleted ? ` ${deleted} saved project${deleted === 1 ? " no longer exists" : "s no longer exist"} and will be dropped on save.` : ""}
      </p>
      <div className="mt-3 flex justify-end">
        <Button variant="primary" disabled={!plan.canSave || save.isPending} onClick={() => save.mutate({ path: `/actors/${actor.id}`, body: { projectIds: plan.projectIds } })} data-testid="projects-save">
          Save projects
        </Button>
      </div>
      <ErrorNote error={save.error} />
    </div>
  );
}

function lines(s: string): string[] {
  return s
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
}

/** "When this seat is limited": the fallback toggle and ordered runtime list (bring-your-own-seats §5.3).
 *  Saved on its own, as the only key of `agentConfig`, so the main Save never sends `fallback` and a
 *  non-person keeps the server's 403 (service.ts updateActor) instead of tripping it by accident. */
function FallbackControl({ actor, me, nodes }: { actor: ActorDTO; me: ActorDTO; nodes: NodeDTO[] }) {
  const saved = actor.agentConfig?.fallback ?? { enabled: false, runtimes: [] as FallbackRuntime[] };
  const save = useAgentConfig();
  const [enabled, setEnabled] = useState(saved.enabled);
  // The primary can't be its own fallback: drop it from the display (and so from the next save).
  const [rawOrder, setOrder] = useState<FallbackRuntime[]>(saved.runtimes);
  // Derived at render so a primary changed on this page drops out of the list and of the next save.
  const order = withoutPrimary(rawOrder, actor.runtime);
  const apiKey = actor.agentConfig?.auth === "api_key";
  const editable = me.kind === "human" && canEditRuntime(me, actor);
  const apiKeyBlocks = enabled && actor.agentConfig?.auth === "api_key";
  const addable = fallbackCandidates(actor.runtime).filter((r) => !order.includes(r));
  const dirty = enabled !== saved.enabled || order.join() !== saved.runtimes.join();
  return (
    <fieldset>
      <legend className="text-[12px] font-medium text-ink-2">When this seat is limited</legend>
      <p className="mb-1 text-[12px] text-ink-3">move the task to the next runtime in this list when the primary hits its usage limit</p>
      <div className="space-y-2" data-testid="fallback-control">
        <label className="flex items-center gap-2 text-[13px] text-ink-2">
          <input type="checkbox" checked={enabled} disabled={!editable} onChange={(e) => setEnabled(e.target.checked)} data-testid="fallback-enabled" />
          Fall back to another runtime
        </label>
        {enabled && (
          <>
            <ol className="space-y-1" data-testid="fallback-list">
              {order.map((r, i) => (
                <li key={r} className="flex items-center gap-2 text-[13px]" data-testid={`fallback-item-${r}`}>
                  <span className="w-5 text-ink-3">{i + 1}.</span>
                  <span className="flex-1">{RUNTIME_LABEL[r]} <span className="text-[12px] text-ink-3">{SEAT_STATE_LABEL[runtimeAvailability(nodes, actor.id, r, { apiKey }).state]}</span></span>
                  <Button disabled={!editable || i === 0} onClick={() => setOrder(moveItem(order, i, -1))} aria-label={`Move ${RUNTIME_LABEL[r]} up`} data-testid={`fallback-up-${r}`}>↑</Button>
                  <Button disabled={!editable || i === order.length - 1} onClick={() => setOrder(moveItem(order, i, 1))} aria-label={`Move ${RUNTIME_LABEL[r]} down`} data-testid={`fallback-down-${r}`}>↓</Button>
                  <Button disabled={!editable} onClick={() => setOrder(order.filter((x) => x !== r))} aria-label={`Remove ${RUNTIME_LABEL[r]}`} data-testid={`fallback-remove-${r}`}>Remove</Button>
                </li>
              ))}
            </ol>
            {addable.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {addable.map((r) => (
                  <Button key={r} disabled={!editable} onClick={() => setOrder([...order, r])} data-testid={`fallback-add-${r}`}>+ {RUNTIME_LABEL[r]}</Button>
                ))}
              </div>
            )}
          </>
        )}
        {apiKeyBlocks && <p className="rounded-md bg-signal-soft px-3 py-2 text-[12px] text-ink-2" data-testid="fallback-apikey-warning">This agent runs on an API key, so the hub refuses every fallback run. Switch its auth back to seat first.</p>}
        {!editable && <p className="text-[12px] text-ink-3">Only an admin or this agent's owner (a person) can change this.</p>}
        {editable && (
          <div className="flex justify-end">
            <Button disabled={!dirty || save.isPending} onClick={() => save.mutate({ id: actor.id, agentConfig: { fallback: { enabled, runtimes: order } } })} data-testid="fallback-save">Apply fallback</Button>
          </div>
        )}
        <ErrorNote error={save.error} />
      </div>
    </fieldset>
  );
}

export function AgentConfigForm({ actor, onSaved }: { actor: ActorDTO; onSaved: () => void }) {
  const cfg: AgentConfig = { ...DEFAULT_AGENT_CONFIG, ...(actor.agentConfig ?? {}) };
  const me = useMe().data!;
  const save = useAgentConfig();
  const nodes = useNodes().data ?? [];
  const caps = runtimeCapabilities(actor.runtime);
  const claude = caps.subagents && caps.advisor;
  // A saved api_key stays fixable on a non-Claude runtime: it makes the hub refuse fallback runs.
  const authVisible = claude || cfg.auth === "api_key";
  const seat = isSeatRuntime(actor.runtime) && actor.runtime !== "claude-code" ? actor.runtime : null;
  const reported = seat ? modelChoices(nodes, actor.id, seat) : null;
  // Until a holding node reports models, keep the free-text field so the agent can be configured before its seat connects.
  const choices = reported && reported.models.length > 0 ? reported : null;
  const [model, setModel] = useState(cfg.model ?? "");
  const [advisor, setAdvisor] = useState(cfg.advisorModel ?? "");
  const [subagents, setSubagents] = useState(JSON.stringify(cfg.subagents, null, 2));
  const [budget, setBudget] = useState(cfg.maxBudgetUsd == null ? "" : String(cfg.maxBudgetUsd));
  const [turns, setTurns] = useState(cfg.maxTurns == null ? "" : String(cfg.maxTurns));
  const [mode, setMode] = useState(cfg.permissionMode);
  const [auth, setAuth] = useState(cfg.auth);
  const [allowed, setAllowed] = useState(cfg.allowedTools.join("\n"));
  const [denied, setDenied] = useState(cfg.deniedTools.join("\n"));
  const [mcp, setMcp] = useState(JSON.stringify(cfg.mcpServers, null, 2));
  const [instructions, setInstructions] = useState(cfg.instructions);
  const [mcpError, setMcpError] = useState<string | null>(null);

  const submit = () => {
    let mcpServers: Record<string, McpServerConfig>;
    let subs: Record<string, SubagentConfig>;
    try {
      mcpServers = mcp.trim() ? (JSON.parse(mcp) as Record<string, McpServerConfig>) : {};
      subs = subagents.trim() ? (JSON.parse(subagents) as Record<string, SubagentConfig>) : {};
      setMcpError(null);
    } catch (e) {
      setMcpError(`MCP servers and subagents must be JSON: ${e instanceof Error ? e.message : String(e)}`);
      return;
    }
    save.mutate(
      {
        id: actor.id,
        agentConfig: {
          model: model.trim() || null,
          ...(claude ? { advisorModel: advisor.trim() || null, subagents: subs } : {}),
          maxBudgetUsd: budget.trim() ? Number(budget) : null,
          maxTurns: turns.trim() ? Number(turns) : null,
          permissionMode: mode,
          allowedTools: lines(allowed),
          deniedTools: lines(denied),
          mcpServers,
          instructions,
          ...(authVisible ? { auth } : {}),
        },
      },
      { onSuccess: onSaved },
    );
  };

  return (
    <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
      <div className="grid grid-cols-3 gap-2">
        {choices ? (
          <Field label="Model" hint={model && isClaudeAlias(model) ? "node default will be used (saved model is a Claude alias)" : "reported by the node"}>
            <select className={inputCls} value={model} onChange={(e) => setModel(e.target.value)} data-testid="agent-model-select">
              <option value="">{choices.defaultModel ? `node default (${choices.defaultModel})` : "node default"}</option>
              {model && !choices.models.includes(model) && <option value={model} disabled>{`${model} (not offered by this node)`}</option>}
              {choices.models.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
        ) : (
          <Field label="Model" hint={seat && model && isClaudeAlias(model) ? "node default will be used (saved model is a Claude alias)" : "alias or id; blank = default"}>
            <input className={inputCls} value={model} onChange={(e) => setModel(e.target.value)} placeholder={seat ? "node default" : "sonnet"} />
          </Field>
        )}
        <Field label="Budget / run ($)">
          <input className={inputCls} type="number" min={0} step="0.5" placeholder="No limit" value={budget} onChange={(e) => setBudget(e.target.value)} />
        </Field>
        <Field label="Max turns">
          <input className={inputCls} type="number" min={1} placeholder="No limit" value={turns} onChange={(e) => setTurns(e.target.value)} />
        </Field>
      </div>
      {claude && (
        <>
          <Field label="Advisor" hint="stronger model consulted at decision points; blank = off (e.g. opus with a sonnet main model)">
            <input className={inputCls} value={advisor} onChange={(e) => setAdvisor(e.target.value)} placeholder="opus" />
          </Field>
          <Field label="Subagents" hint="JSON: name → {description, prompt, model, tools}. Default: haiku researcher and test-runner, sonnet reviewer.">
            <textarea className={cx(inputCls, "font-mono text-[12px]")} rows={6} value={subagents} onChange={(e) => setSubagents(e.target.value)} spellCheck={false} />
          </Field>
        </>
      )}
      <Field label="File edits" hint={actor.allowedAreas.length ? "fenced to allowed areas; edits elsewhere are denied" : "unfenced"}>
        <div className="flex gap-1">
          {PERMISSION_MODES.map((m) => (
            <button key={m} type="button" onClick={() => setMode(m)} className={cx("rounded border px-2.5 py-1 font-mono text-[12px]", mode === m ? "border-ink bg-ink text-paper" : "border-rule")} disabled={actor.allowedAreas.length > 0}>
              {m === "acceptEdits" ? "auto-approve edits" : "manual (rules only)"}
            </button>
          ))}
        </div>
      </Field>
      {authVisible && <Field label="Auth" hint={auth === "api_key" ? "calls the Anthropic API with this node's own ANTHROPIC_API_KEY" : "runs on the owner's Claude Code seat"}>
        <div className="flex gap-1">
          {AUTH_MODES.map((m) => {
            // Only admins switch an agent *to* api_key (service.ts): it spends the node's shared
            // ANTHROPIC_API_KEY budget. Switching back to seat, or leaving an already-api_key
            // agent as is, needs no elevated access.
            const blocked = m === "api_key" && cfg.auth !== "api_key" && me.access !== "admin";
            return (
              <button
                key={m}
                type="button"
                onClick={() => setAuth(m)}
                className={cx("rounded border px-2.5 py-1 font-mono text-[12px]", auth === m ? "border-ink bg-ink text-paper" : "border-rule")}
                disabled={blocked}
                title={blocked ? "only admins switch an agent to api_key" : undefined}
              >
                {m === "api_key" ? "api key" : "seat"}
              </button>
            );
          })}
        </div>
      </Field>}
      <FallbackControl actor={actor} me={me} nodes={nodes} />
      <div className="grid grid-cols-2 gap-2">
        <Field label="Allowed tools" hint="one rule per line">
          <textarea className={cx(inputCls, "font-mono text-[12px]")} rows={5} value={allowed} onChange={(e) => setAllowed(e.target.value)} />
        </Field>
        <Field label="Denied tools">
          <textarea className={cx(inputCls, "font-mono text-[12px]")} rows={5} value={denied} onChange={(e) => setDenied(e.target.value)} />
        </Field>
      </div>
      <Field label="MCP servers" hint={'JSON; secrets only as "${ENV_VAR}" (set on the owner\'s laptop). Roomate itself is added automatically.'}>
        <textarea className={cx(inputCls, "font-mono text-[12px]")} rows={6} value={mcp} onChange={(e) => setMcp(e.target.value)} spellCheck={false} />
      </Field>
      <Field label="Standing instructions" hint="appended to every run's system prompt">
        <textarea className={inputCls} rows={4} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="e.g. Use Playwright. Never touch migrations. Open PRs against main." />
      </Field>
      <ErrorNote error={mcpError ?? save.error} />
      <div className="flex justify-end gap-2">
        <Button variant="primary" onClick={submit} disabled={save.isPending}>
          Save
        </Button>
      </div>
    </div>
  );
}
